import {
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, SwapOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";
import AddEditStockItem from "./AddEditStockItem";
import StockSummaryStrip from "./StockSummaryStrip";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const CATEGORIES = [
  "Stationery",
  "Furniture",
  "Cleaning",
  "Sports",
  "Lab",
  "Electronics",
  "Other",
];

/** Admin-only catalogue: what the school holds and how much of it is left. */
const StockItems = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState();
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [delta, setDelta] = useState(0);
  const [adjustNote, setAdjustNote] = useState("");
  const [savingAdjust, setSavingAdjust] = useState(false);

  const items = useQuery(
    ["/school-stock/items", search, category, lowStockOnly],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category) params.append("category", category);
      if (lowStockOnly) params.append("low_stock", "true");
      params.append("is_active", "all");
      const res = await axios.get(
        `${serverUrl}/school-stock/items?${params.toString()}`,
        { headers: authHeader() },
      );
      return res.data;
    },
  );

  // Server-side aggregation, so the counts cover the whole catalogue even when
  // the table below is filtered.
  const summary = useQuery("/school-stock/items/summary", async () => {
    const res = await axios.get(`${serverUrl}/school-stock/items/summary`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const refresh = () => {
    queryClient.invalidateQueries("/school-stock/items");
    queryClient.invalidateQueries("/school-stock/items/summary");
  };

  const openAdjust = (record) => {
    setAdjusting(record);
    setDelta(0);
    setAdjustNote("");
  };

  const handleAdjust = async () => {
    if (!delta) {
      message.error("Enter a non-zero amount");
      return;
    }
    setSavingAdjust(true);
    try {
      await axios.patch(
        `${serverUrl}/school-stock/items/${adjusting._id}/adjust`,
        { delta, note: adjustNote || undefined },
        { headers: authHeader() },
      );
      message.success(
        `${adjusting.name} ${delta > 0 ? "increased" : "reduced"} by ${Math.abs(
          delta,
        )}`,
      );
      setAdjusting(null);
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    } finally {
      setSavingAdjust(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await axios.delete(
        `${serverUrl}/school-stock/items/${record._id}`,
        { headers: authHeader() },
      );
      message.success(
        res?.data?.result?.message ||
          `${record.name} removed from the catalogue`,
      );
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    }
  };

  const columns = [
    {
      title: "Item",
      width: 40,
      render: (record) => (
        <div>
          <div className="font-semibold">
            {record.name}
            {!record.is_active && (
              <Tag className="ml-2" color="default">
                archived
              </Tag>
            )}
          </div>
          {record.description && (
            <div className="text-xs text-gray-500">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      width: 40,
      render: (record) => record.category || "—",
    },
    {
      title: "In hand",
      width: 40,
      render: (record) => {
        const low = record.quantity_in_hand <= record.low_stock_threshold;
        return (
          <div className="flex items-center gap-2">
            <span className="font-semibold">
              {record.quantity_in_hand} {record.unit}
            </span>
            {record.quantity_in_hand <= 0 ? (
              <Tag color="red">out of stock</Tag>
            ) : (
              low && <Tag color="orange">low</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Alert at",
      width: 40,
      render: (record) => record.low_stock_threshold,
    },
    {
      title: "Action",
      width: 40,
      align: "center",
      render: (record) => (
        <div className="flex items-center justify-center gap-3">
          <Tooltip title="Adjust quantity">
            <Button
              size="small"
              shape="circle"
              icon={
                <SwapOutlined
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              }
              onClick={() => openAdjust(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              shape="circle"
              icon={
                <EditOutlined
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              }
              onClick={() => {
                setEditing(record);
                setFormOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title={`Remove ${record.name}?`}
            description="This can't be undone."
            okText="Remove"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Remove">
              <Button
                size="small"
                shape="circle"
                danger
                icon={
                  <DeleteOutlined
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                }
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="pt-6">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex items-center justify-between pb-8">
          <div className="text-2xl text-orange-500 font-semibold">
            Stock Inventory
          </div>
          <Button
            type="primary"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            New Item
          </Button>
        </div>

        <StockSummaryStrip
          loading={summary.isLoading}
          tiles={[
            {
              label: "Items",
              value: summary?.data?.result?.totalItems ?? 0,
            },
            {
              label: "Units in hand",
              value: summary?.data?.result?.totalUnitsInHand ?? 0,
            },
            {
              label: "Low stock",
              value: summary?.data?.result?.lowStockCount ?? 0,
              tone: "pending",
            },
            {
              label: "Out of stock",
              value: summary?.data?.result?.outOfStockCount ?? 0,
              tone: "danger",
            },
          ]}
        />

        <div className="flex gap-4 pb-4">
          <Input.Search
            allowClear
            placeholder="Search items"
            style={{ width: 260 }}
            onSearch={setSearch}
          />
          <Select
            allowClear
            placeholder="Category"
            style={{ width: 180 }}
            value={category}
            onChange={setCategory}
            options={CATEGORIES.map((c) => ({ label: c, value: c }))}
          />
          <Button
            type={lowStockOnly ? "primary" : "default"}
            onClick={() => setLowStockOnly((prev) => !prev)}
          >
            Low stock only
          </Button>
        </div>

        <Table
          rowKey="_id"
          className="rounded-2xl"
          loading={items.isLoading}
          columns={columns}
          scroll={{ x: 900 }}
          dataSource={items?.data?.result || []}
        />
      </div>

      <AddEditStockItem
        open={formOpen}
        item={editing}
        onClose={() => setFormOpen(false)}
        onSaved={refresh}
      />

      <Modal
        title={`Adjust stock — ${adjusting?.name || ""}`}
        open={!!adjusting}
        onCancel={() => setAdjusting(null)}
        onOk={handleAdjust}
        okText="Apply"
        confirmLoading={savingAdjust}
        destroyOnClose
      >
        <p className="pb-3 text-gray-600">
          Currently <b>{adjusting?.quantity_in_hand}</b> {adjusting?.unit} in
          hand. Use a positive amount to receive new supply, a negative one to
          correct a miscount.
        </p>
        <InputNumber
          className="w-full"
          precision={0}
          value={delta}
          onChange={(value) => setDelta(value || 0)}
          placeholder="e.g. 25 or -3"
        />
        <Input.TextArea
          style={{ marginTop: 12 }}
          rows={2}
          value={adjustNote}
          onChange={(e) => setAdjustNote(e.target.value)}
          placeholder="Note (optional) — e.g. new supply received from vendor"
        />
        {delta !== 0 && (
          <p className="pt-3 text-gray-600">
            New quantity will be{" "}
            <b>{(adjusting?.quantity_in_hand || 0) + delta}</b>{" "}
            {adjusting?.unit}.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default StockItems;
